import React, { useState, useEffect } from 'react';

/**
 * Generic unit converter - pure Tailwind, no MUI.
 *
 *  <UnitConverter
 *    title="Length"
 *    units={[{ key: 'feet', label: 'Feet' }, ...]}
 *    factors={{ feet: 1, met: 0.3048, ... }}    // linear
 *    explanation={...}
 *  />
 *  // or non-linear: each unit declares toBase + fromBase functions
 */

export const UnitConverter = ({ title, units, factors, explanation, explanationTitle }) => {
  const [values, setValues] = useState(() => {
    const init = {};
    units.forEach((u) => { init[u.key] = ''; });
    return init;
  });
  const [activeKey, setActiveKey] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isLinear = !!factors;

  const onChange = (fromKey) => (e) => {
    const raw = e.target.value;
    setActiveKey(fromKey);

    if (raw === '') {
      const cleared = {};
      units.forEach((u) => { cleared[u.key] = ''; });
      setValues(cleared);
      return;
    }
    const num = parseFloat(raw);
    if (Number.isNaN(num)) {
      setValues((prev) => ({ ...prev, [fromKey]: raw }));
      return;
    }

    let baseValue;
    if (isLinear) baseValue = num / factors[fromKey];
    else {
      const unit = units.find((u) => u.key === fromKey);
      baseValue = unit.toBase(num);
    }

    const next = {};
    units.forEach((u) => {
      if (u.key === fromKey) {
        next[u.key] = raw;
      } else {
        const v = isLinear ? baseValue * factors[u.key] : u.fromBase(baseValue);
        next[u.key] = Number.isFinite(v) ? formatNum(v) : '';
      }
    });
    setValues(next);
  };

  const onClear = () => {
    const cleared = {};
    units.forEach((u) => { cleared[u.key] = ''; });
    setValues(cleared);
    setActiveKey(null);
  };

  const hasAnyValue = Object.values(values).some((v) => v !== '');

  return (
    <div>
      {/* Section header */}
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {title && (
            <h2 className="text-[20px] sm:text-[24px] font-bold leading-tight tracking-[-0.02em] text-fg-primary">
              {title}
            </h2>
          )}
          <div aria-hidden className="mt-2 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />
        </div>

        {hasAnyValue && (
          <button
            type="button"
            onClick={onClear}
            className="
              inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5
              text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary
              hover:text-amber hover:bg-amber/8
              transition-colors duration-150
            "
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
            Clear all
          </button>
        )}
      </div>

      {/* Converter grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
        {units.map((u) => (
          <UnitCard
            key={u.key}
            unit={u}
            value={values[u.key]}
            onChange={onChange(u.key)}
            isActive={activeKey === u.key}
            isFilled={values[u.key] !== '' && activeKey !== u.key}
          />
        ))}
      </div>

      {/* Explanation */}
      {explanation && (
        <div className="mt-6 rounded-xl border border-border-subtle bg-surface-elevated/60 p-4">
          <div className="mb-2 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-amber" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">
              {explanationTitle || `About ${title?.toLowerCase() || 'the units'}`}
            </span>
          </div>
          <p className="text-[13px] leading-[1.6] text-fg-secondary">{explanation}</p>
        </div>
      )}
    </div>
  );
};

/* ─────────── Single unit input card ─────────── */

function UnitCard({ unit, value, onChange, isActive, isFilled }) {
  const [focused, setFocused] = useState(false);

  return (
    <label
      className={`
        group relative flex flex-col rounded-xl border bg-surface-overlay/50
        px-3.5 py-3 cursor-text
        transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out-soft
        ${focused
          ? 'border-amber bg-surface-elevated shadow-[0_0_0_3px_rgba(251,191,36,0.18)]'
          : isActive
            ? 'border-amber/55 bg-surface-elevated'
            : isFilled
              ? 'border-border-strong bg-surface-elevated/70'
              : 'border-border-divider hover:border-border-strong'}
      `}
    >
      {/* Active accent bar at top - slides in when this is the source field */}
      <span
        aria-hidden
        className={`
          pointer-events-none absolute inset-x-0 top-0 h-[2px]
          bg-gradient-to-r from-amber via-amber-light to-amber rounded-t-xl
          origin-left scale-x-0 transition-transform duration-300 ease-out-soft
          ${isActive ? 'scale-x-100' : ''}
        `}
      />

      <span className={`
        text-[10px] font-bold uppercase tracking-[0.16em] leading-none mb-1.5
        transition-colors duration-200
        ${focused || isActive ? 'text-amber' : 'text-fg-tertiary'}
      `}>
        {unit.label}
      </span>

      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="0"
        className="
          w-full bg-transparent
          text-[18px] font-semibold tracking-[-0.01em] leading-tight
          text-fg-primary placeholder:text-fg-tertiary/50
          outline-none border-0 p-0
          [appearance:textfield]
          [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0
          [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0
        "
      />
    </label>
  );
}

/* ─────────── Number formatter ─────────── */

function formatNum(n) {
  if (n === 0) return 0;
  const abs = Math.abs(n);
  if (abs >= 100) return +n.toFixed(2);
  if (abs >= 1) return +n.toFixed(4);
  if (abs >= 0.001) return +n.toFixed(6);
  return n.toExponential(3);
}
